import { useEffect, useCallback } from "react";
import {
  Layout,
  Page,
  Card,
  InlineGrid,
  Text,
  BlockStack,
  Button,
  Spinner,
  InlineStack,
  Link,
  Badge,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchFuelReportDetails,
  setDestroy,
} from "./fuel-report-container.slice";
import FuelReportViewSkeleton from "./fuel-report-view-skeleton";
import DataTable from "components/datatable/Datatable";
import { AppDispatch, RootState } from "configureStore";
import PrettyDate from "components/format/PrettyDate";
import { Column } from "types/Datatable";
import { formatPrettyDate, escapeHtml } from "utils/helperFunctions";
import {
  getMonthMileage,
  getNonWorkPurposeKm,
  isFullElectricVehicle,
} from "utils/fuelReportUtils";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { FuelReport } from "types/FuelReport";
import "./styles.scss";
import "components/stack-narrow-section-first/stack-narrow-section-first.scss";
import { API_URL } from "../../constants";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";

function FuelReportContainer({ source = "" }) {
  const { t } = useTranslation();
  const { companyId, reportId } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const { role } = useSelector((state: RootState) => state.authReducer);

  const { fuelReportEntity, isLoading, isError, requestedReportId } =
    useSelector((state: RootState) => state.fuelReportContainerReducer);

  const loadFuelReportData = useCallback(() => {
    if (companyId && reportId) {
      dispatch(fetchFuelReportDetails({ companyId, reportId }));
    }
  }, [dispatch, companyId, reportId]);

  useEffect(() => {
    loadFuelReportData();
    return () => {
      dispatch(setDestroy());
    };
  }, [loadFuelReportData]);

  // Edit button logic
  const handleEditFuelReportClick = () => {
    dispatch(setModalxComponent("FuelReportForm"));
    dispatch(setModalxComponentProps({ id: reportId, source }));
  };

  const handleOnClick = () => {
    dispatch(setModalxComponent("FuelReportDocumentForm"));
    dispatch(
      setModalxComponentProps({
        user: role?.user,
        subject: "fuel",
        signType: "Click",
        managerSigner: role?.manager,
        fuelReport: reportId,
      })
    );
  };

  const handleRefuseFuelReport = () => {
    dispatch(setModalxComponent("RefuseFuelReportConfirm"));
    dispatch(setModalxComponentProps({ reportId }));
  };

  // The loaded report matches the route only when its id equals reportId —
  // guards against briefly showing the previous report's data after navigating
  // to a different one (the route reconciles instead of remounting).
  const isReportReady =
    !!fuelReportEntity && fuelReportEntity.id === reportId;

  const backUrl =
    source === "profile"
      ? `/app/${companyId}/profile/${role?.user}/fuel-reports`
      : `/app/${companyId}/fuel-reports`;

  // If loading the requested report failed, show an error with a retry path
  // instead of an endless skeleton. requestedReportId scopes the error to the
  // report that actually failed (the slice clears it when a new fetch starts),
  // so a stale error never blocks a different report, and a failed background
  // refetch (when valid data is already shown) keeps the page visible.
  if (isError && requestedReportId === reportId && !isReportReady) {
    return (
      <Page
        fullWidth
        backAction={{ content: t("fuel_reports"), url: backUrl }}
        title={t("fuel_reports")}
      >
        <Card>
          <BlockStack gap="300">
            <Text as="p" tone="critical">
              {t("not_fetch", { defaultValue: "Could not load fuel report" })}
            </Text>
            <div>
              <Button onClick={loadFuelReportData}>
                {t("retry", { defaultValue: "Retry" })}
              </Button>
            </div>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  if (!isReportReady) {
    return <FuelReportViewSkeleton />;
  }

  // Prepare DataTable data for fuelRecords
  const fuelRecordsTableData = (fuelReportEntity.fuelRecords || []).map(
    (item: any, idx: number) => ({
      id: item.id,
      "#": idx + 1,
      dateTime: item.dateTime || "-",
      cardUser: item.cardUser || "-",
      cardNumber: item.cardNumber || "-",
      place: item.place || "-",
      receiptNumber: item.receiptNumber || "-",
      Name: item.Name || "-",
      quantity: item.quantity,
      amount:
        (item.amountWithoutDiscount || 0) -
        (item.discountAmount || 0) -
        (item.receiptFees || 0) +
        (item.discountsOnTaxes || 0),
    })
  );

  // Prepare manual fuel expenses and append to fuelRecordsTableData
  const manualExpenses = (fuelReportEntity as FuelReport).manualExpenses || [];
  const manualFuelExpenses = manualExpenses
    .filter((item: any) => item.expenseType === "fuel")
    .map((item: any, idx: number) => {
      // Extract file info
      const fileId = typeof item.file === "object" && item.file?.id 
        ? item.file.id 
        : typeof item.file === "string" 
          ? item.file 
          : null;
      const fileName = typeof item.file === "object" 
        ? (item.file?.name || item.file?.title || null)
        : null;
      
      // Format file as HTML link if exists
      const fileHtml = fileId 
        ? `<a href="${API_URL}/${companyId}/files/download/${fileId}" target="_blank" style="color: #0066cc; text-decoration: underline;">${escapeHtml(fileName) || "-"}</a>`
        : "-";
      
      return {
        id: item.id,
        "#": fuelRecordsTableData.length + idx + 1,
        dateTime: item.date ? formatPrettyDate(item.date, "lt-LT", false) : "-",
        cardUser: "-",
        cardNumber: "-",
        place: "-",
        receiptNumber: "-",
        Name: "-",
        quantity: item.quantity || 0,
        amount: item.amount || 0,
        file: fileHtml,
      };
    });

  // Combine fuel records with manual fuel expenses
  const combinedFuelRecordsTableData = [...fuelRecordsTableData, ...manualFuelExpenses];

  // No `mobileView` here on purpose: without it a phone card lists every named column, receipt link included.
  const fuelRecordsColumns = [
    { key: "#" },
    { key: "dateTime", name: t("date") },
    { key: "cardUser", name: t("card_user") },
    { key: "cardNumber", name: t("card_no") },
    { key: "place", name: t("place") },
    { key: "receiptNumber", name: t("receipt_number") },
    { key: "Name", name: t("fuel_type") },
    { key: "quantity", name: t("quantity"), type: "number" },
    { key: "amount", name: t("amount"), type: "currency" },
    { key: "file", name: t("file"), type: "html_node" },
  ] as Column[];

  // Prepare DataTable data for additionalExpensesRecords
  const additionalExpensesTableData = (
    (fuelReportEntity as FuelReport).additionalExpensesRecords || []
  ).map((item: any, idx: number) => ({
    id: item.id,
    "#": idx + 1,
    dateTime: item.dateTime || "-",
    cardUser: item.cardUser || "-",
    cardNumber: item.cardNumber || "-",
    place: item.place || "-",
    receiptNumber: item.receiptNumber || "-",
    Name: item.Name || "-",
    quantity: item.quantity,
    amount:
      (item.amountWithoutDiscount || 0) -
      (item.discountAmount || 0) -
      (item.receiptFees || 0) +
      (item.discountsOnTaxes || 0),
  }));

  // Prepare manual other expenses and append to additionalExpensesTableData
  const manualOtherExpenses = manualExpenses
    .filter((item: any) => item.expenseType === "other")
    .map((item: any, idx: number) => {
      // Extract file info
      const fileId = typeof item.file === "object" && item.file?.id 
        ? item.file.id 
        : typeof item.file === "string" 
          ? item.file 
          : null;
      const fileName = typeof item.file === "object" 
        ? (item.file?.name || item.file?.title || null)
        : null;
      
      // Format file as HTML link if exists
      const fileHtml = fileId 
        ? `<a href="${API_URL}/${companyId}/files/download/${fileId}" target="_blank" style="color: #0066cc; text-decoration: underline;">${escapeHtml(fileName) || "-"}</a>`
        : "-";
      
      return {
        id: item.id,
        "#": additionalExpensesTableData.length + idx + 1,
        dateTime: item.date ? formatPrettyDate(item.date, "lt-LT", false) : "-",
        cardUser: "-",
        cardNumber: "-",
        place: "-",
        receiptNumber: "-",
        Name: "-",
        quantity: 0,
        amount: item.amount || 0,
        file: fileHtml,
      };
    });

  // Combine additional expenses with manual other expenses
  const combinedAdditionalExpensesTableData = [...additionalExpensesTableData, ...manualOtherExpenses];

  const additionalExpensesColumns = [
    { key: "#" },
    { key: "dateTime", name: t("date") },
    { key: "cardUser", name: t("card_user") },
    { key: "cardNumber", name: t("card_no") },
    { key: "place", name: t("place") },
    { key: "receiptNumber", name: t("receipt_number") },
    { key: "Name", name: t("name") },
    { key: "amount", name: t("amount"), type: "currency" },
    { key: "file", name: t("file"), type: "html_node" },
  ] as Column[];

  // Main report details
  const details = fuelReportEntity;
  const asset: any =
    details.asset && typeof details.asset === "object" ? details.asset : {};
  // Handle fuelCard as array or single value
  let fuelCardDisplay = "";
  if (Array.isArray(details.fuelCards)) {
    fuelCardDisplay = details.fuelCards
      .map((card: any) => card?.name || "")
      .filter(Boolean)
      .join(", ");
  }

  const user: any =
    details.user && typeof details.user === "object" ? details.user : {};

  const {
    id,
    createdAt,
    title,
    name,
    category,
    user: fileUser,
    series,
    number,
    categories,
    filesTemplateCustomField,
    isApproved,
    fileId,
    isSigned,
    relatedTo,
    signType,
    dokobit,
    signersList,
    templateId,
    signingStepsRequired,
  } = fuelReportEntity?.files?.[0] || {};

  const isUserTemplate = templateId?.subject === "user";
  const isUserUploader = fileUser?.id === role?.user;

  const ownerSigned = signersList?.some(
    (signer: any) =>
      signer?.user?.id === fileUser?.id && signer.status === "signed"
  );

  const sentSignRequestManager = signersList?.some(
    (signer: any) => signer.position === "manager"
  );

  const Title = `[${details.period || ""}] ${asset?.name || ""}`;

  // Check if user has permission to access fuel reports route (MUST be before RefuseReportButtonNode)
  const canAccessFuelReports =
    role?.permissionsgroups?.includes("Master") ||
    role?.permissionsgroups?.includes("Access fuel reports");

  // Edit button node - always enabled for users with fuel reports permission (but only on main route, not profile)
  const isEditLocked =
    isSigned && !(canAccessFuelReports && source !== "profile");
  const FuelReportEditNode = (
    <Button disabled={isEditLocked} onClick={handleEditFuelReportClick}>
      {t("edit_fuel_report")}
    </Button>
  );

  // Refuse button - only visible if signed AND user has permission AND not from profile
  const RefuseReportButtonNode =
    canAccessFuelReports && source !== "profile" && isSigned ? (
      <Button onClick={handleRefuseFuelReport} tone="critical">
        {t("refuse_report")}
      </Button>
    ) : null;

  const downloadUrl = `${API_URL}/${companyId}/files/download/${id}`;

  const handleClickSign = (fileId: string) => {
    dispatch(setModalxComponent("ClickToSignConfirm"));
    dispatch(setModalxComponentProps({ fileId }));
  };

  const handleEditFile = (id: string) => {
    dispatch(setModalxComponent("FuelReportDocumentForm"));
    dispatch(
      setModalxComponentProps({
        id,
        subject: "fuel",
        user: role?.user,
      })
    );
  };

  const handleUploadClick = async (id: string, user: any) => {
    dispatch(setModalxComponent("AddClickSigner"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
        manager: user,
        signType: "Click",
      })
    );
  };

  // The reason a signed report cannot be edited is plain text: a tooltip on a disabled
  // button never shows on a touch screen.
  const signedEditReason = (
    <Text as="p" variant="bodySm" tone="subdued">
      {t("document_is_signed_edit_is_not_allowed")}
    </Text>
  );

  const TemplateEditNode = isSigned ? (
    <BlockStack gap="100" inlineAlign="center">
      <Button disabled>{t("edit")}</Button>
      {signedEditReason}
    </BlockStack>
  ) : (
    <Button
      onClick={() => {
        handleEditFile(id);
      }}
      disabled={isSigned}
    >
      {t("edit")}
    </Button>
  );

  // Primary action with both Edit and Refuse buttons
  const primaryActionMarkup = (
    <InlineStack gap="300">
      {FuelReportEditNode}
      {RefuseReportButtonNode}
    </InlineStack>
  );

  return (
    <Page
      fullWidth
      backAction={{
        content: t("fuel_reports"),
        url:
          source === "profile"
            ? `/app/${companyId}/profile/${role?.user}/fuel-reports`
            : `/app/${companyId}/fuel-reports`,
      }}
      title={Title}
      titleMetadata={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isLoading && <Spinner size="small" />}
          <AuditLogPopover
            companyId={companyId!}
            subjectType="FuelReport"
            subjectId={reportId}
            label={t("history", { defaultValue: "History" })}
          />
        </div>
      }
      // A disabled button says nothing on a touch screen, so the reason is written under the title.
      subtitle={isEditLocked ? t("document_is_signed_edit_is_not_allowed") : undefined}
      primaryAction={primaryActionMarkup}
    >
      <div className="stack-narrow-section-first">
        <Layout>
          {/* Left: DataTable of all fuel records */}
          <Layout.Section>
            <Card>
              <Text as="h2" variant="headingSm">
                {t("fuel_records")}
              </Text>
              <DataTable
                collection={combinedFuelRecordsTableData}
                columns={fuelRecordsColumns}
              />
            </Card>
            {/* DataTable of additional expenses records */}
            <Card>
              <Text as="h2" variant="headingSm">
                {t("additional_expenses")}
              </Text>
              <DataTable
                collection={combinedAdditionalExpensesTableData}
                columns={additionalExpensesColumns}
              />
            </Card>
          </Layout.Section>

          {/* Right: Details and actions */}
          <Layout.Section variant="oneThird">
            <BlockStack>
              {/* Details Card */}
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <InlineGrid>
                    <Text as="p" variant="bodyMd">
                      <strong>{t("date")}:</strong> {details.period || "-"}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      <strong>{t("asset")}:</strong> {asset?.name || "-"}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      <strong>{t("fuel_card")}:</strong> {fuelCardDisplay}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      <strong>{t("user")}:</strong> {user?.name || "-"}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      <strong>{t("end_of_month_km")}:</strong>{" "}
                      {details.endOfMonthKm ?? "-"}
                    </Text>
                    {!isFullElectricVehicle(asset?.fuelType) && (
                      <Text as="p" variant="bodyMd">
                        <strong>{t("end_of_month_ltr")}:</strong>{" "}
                        {details.endOfMonthLtr ?? "-"}
                      </Text>
                    )}
                    {isFullElectricVehicle(asset?.fuelType) && (
                      <>
                        <Text as="p" variant="bodyMd">
                          <strong>{t("work_purpose_km")}:</strong>{" "}
                          {details.workPurposeKm ?? "-"}
                        </Text>
                        <Text as="p" variant="bodyMd">
                          <strong>{t("non_work_purpose_km")}:</strong>{" "}
                          {details.startOfMonthKm != null &&
                          details.endOfMonthKm != null &&
                          details.workPurposeKm != null
                            ? getNonWorkPurposeKm(
                                getMonthMileage(
                                  details.startOfMonthKm,
                                  details.endOfMonthKm
                                ),
                                details.workPurposeKm
                              )
                            : "-"}
                        </Text>
                      </>
                    )}
                    <Text as="p" variant="bodyMd">
                      <strong>{t("ev_charge_kw")}:</strong>{" "}
                      {details.evChargeKw ?? "-"}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      <strong>{t("ev_charge_amount")}:</strong>{" "}
                      {details.evChargeAmount ?? "-"}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      <strong>{t("fuel_type")}:</strong>{" "}
                      {asset?.fuelType || "-"}
                    </Text>
                  </InlineGrid>
                </BlockStack>
              </Card>
            </BlockStack>
            <BlockStack>
              <div style={{ marginTop: "50px" }}>
                <BlockStack gap="500">
                  {/* box 1 */}
                  <div className="card-wrapper">
                    <div
                      className={`card-box ${
                        templateId
                          ? "border-green"
                          : !templateId
                          ? "border-yellow"
                          : "border-grey"
                      }`}
                    >
                      <Card roundedAbove="sm">
                        <Text as="h2" variant="headingSm">
                          {t("generate_fuel_report")}
                        </Text>
                        {fuelReportEntity?.files?.length < 1 && (
                          <InlineStack align="start">
                            <Button onClick={() => handleOnClick()}>
                              {t("files_build_pdf")}
                            </Button>
                          </InlineStack>
                        )}

                        {fuelReportEntity?.files?.length > 0 && (
                          <>
                            <InlineStack align="center">
                              <div style={{ marginBottom: "5px" }}>
                                <Link url={downloadUrl} target="_blank">
                                  {fuelReportEntity?.files[0].name ||
                                    fuelReportEntity?.files[0].title}
                                </Link>
                              </div>
                            </InlineStack>
                            <InlineStack align="center">
                              {TemplateEditNode}
                            </InlineStack>
                          </>
                        )}
                      </Card>
                    </div>
                    <div className="connector-wrapper">
                      <div className="vertical-line"></div>
                      <div className="arrow-down"></div>
                    </div>
                  </div>
                  {/* box 2 */}
                  <div className="card-wrapper">
                    <div
                      className={`card-box ${
                        isSigned
                          ? "border-green"
                          : templateId
                          ? "border-yellow"
                          : "border-grey"
                      }`}
                    >
                      <Card roundedAbove="sm">
                        <Text as="h2" variant="headingSm">
                          {t("sign_the_fuel_report")}
                        </Text>

                        <>
                          {signersList?.length > 0 && (
                            <div className="content_break content_margin">
                              {signersList?.map((signer: any) => (
                                <Text variant="bodyMd" as="p" key={signer.id}>
                                  {t("sign_status")} : {signer?.user?.name}{" "}
                                  {signer.status === "signed" ? (
                                    <>
                                      <PrettyDate
                                        dateString={signer.updatedAt}
                                        showTime={true}
                                      />{" "}
                                      <Badge tone="success">{t("signed")}</Badge>{" "}
                                    </>
                                  ) : signer.status === "pending" ? (
                                    <>
                                      <PrettyDate
                                        dateString={signer.createdAt}
                                        showTime={true}
                                      />{" "}
                                      <Badge tone="attention">
                                        {t("pending")}
                                      </Badge>
                                      {signer?.user?.id === role?.user && (
                                        <>
                                          <Button
                                            onClick={() => handleClickSign(id)}
                                            accessibilityLabel="Preview"
                                          >
                                            {t("sign")}
                                          </Button>{" "}
                                        </>
                                      )}
                                    </>
                                  ) : signer.status === "rejected" ? (
                                    <>
                                      <PrettyDate
                                        dateString={signer.updatedAt}
                                        showTime={true}
                                      />{" "}
                                      <Badge tone="critical">
                                        {t("declined")}
                                      </Badge>
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </Text>
                              ))}
                            </div>
                          )}
                          {templateId?.isApprovalRequired &&
                            isUserTemplate &&
                            isUserUploader &&
                            !isSigned &&
                            ownerSigned &&
                            !sentSignRequestManager && (
                              <InlineStack align="center">
                                <Button
                                  onClick={() =>
                                    handleUploadClick(
                                      id,
                                      fileUser?.companyuser?.manager?.id
                                    )
                                  }
                                  accessibilityLabel="Preview"
                                >
                                  {t("sign_request_to_manager")}
                                </Button>
                              </InlineStack>
                            )}
                          <InlineStack align="center">
                            {isSigned && (
                              <Badge tone="success">{t("signed")}</Badge>
                            )}
                          </InlineStack>
                        </>
                      </Card>
                    </div>
                  </div>
                </BlockStack>
              </div>
            </BlockStack>
          </Layout.Section>
        </Layout>
      </div>
    </Page>
  );
}

export default FuelReportContainer;
